import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" component="h1" gutterBottom>
            Privacy Policy
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Last updated on November 29, 2025
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          This Privacy Policy describes how GoTruTax ("we", "us", or "our") collects, uses, and protects your personal information when you use our website and services. We are committed to protecting your privacy and complying with the General Data Protection Regulation (GDPR) and other applicable data protection laws.
        </Typography>
        <Box sx={{ my: 2 }}>
          <Typography variant="body2">
            For more information, please review our{' '}
            <a href="/cookie-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline' }}>Cookie Policy</a>{' '}and{' '}
            <a href="/gdpr-rights" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline' }}>GDPR Rights</a>{' '}pages. These documents explain your rights and our practices regarding cookies and data subject rights, as required by law.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h4" gutterBottom>
          1. Information We Collect
        </Typography>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Personal Information You Provide</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              We collect information you provide directly to us, including:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Account Information: Name, email address, password, company details" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Business Information: Tax ID, business registration, financial data" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Communication: Messages, support requests, feedback" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Profile Information: Preferences, settings, uploaded documents" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Information Collected Automatically</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              When you use our services, we automatically collect certain information, including:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Usage Data: Pages visited, features used, time spent on site" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Device Information: IP address, browser type, operating system" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Location Data: General location based on IP address" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Cookies and Tracking: See our Cookie Policy for details" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Information from Third Parties</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              We may receive information from third-party services you connect to our platform:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Payment processors: Transaction data from third-party payment services" />
              </ListItem>
              <ListItem>
                <ListItemText primary="E-commerce platforms: Order data from integrated third-party services" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Accounting software: Financial data from integrated third-party services" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Social login: Basic profile information from third-party authentication providers" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h4" gutterBottom>
          2. How We Use Your Information
        </Typography>
        <Typography variant="body1" paragraph>
          We use the information we collect for the following purposes:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Service Provision: To provide tax calculation and compliance services" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Account Management: To create and manage your account" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Communication: To respond to inquiries and provide support" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Legal Compliance: To comply with tax laws and regulations" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Improvement: To analyze usage and improve our services" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Security: To protect against fraud and unauthorized access" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Marketing: To send relevant updates and promotional materials (with consent)" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h4" gutterBottom>
          3. Legal Basis for Processing
        </Typography>
        <Typography variant="body1" paragraph>
          GoTruTax processes your personal data in accordance with applicable data protection laws, including the General Data Protection Regulation (GDPR). The legal bases for processing your personal data may include the necessity of processing for the performance of a contract, compliance with legal obligations, protection of vital interests, consent, and legitimate interests pursued by GoTruTax or a third party. Where consent is required, you have the right to withdraw it at any time without affecting the lawfulness of processing based on consent before its withdrawal.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h4" gutterBottom>
          4. Information Sharing and Disclosure
        </Typography>
        <Typography variant="body1" paragraph>
          We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:
        </Typography>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Service Providers</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              We share information with trusted third-party service providers who assist us in operating our website and conducting our business. All such providers are contractually obligated to maintain the confidentiality, integrity, and security of your information and to comply with all applicable laws. We do not disclose the names of these providers in this policy, but you may request more information by contacting support@gotrutax.com.
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Cloud hosting providers" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Payment processors" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Analytics services" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Email service providers" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Legal Requirements</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              We may disclose your information if required by law or to protect our rights:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="To comply with legal obligations or court orders" />
              </ListItem>
              <ListItem>
                <ListItemText primary="To protect against fraud or security threats" />
              </ListItem>
              <ListItem>
                <ListItemText primary="To enforce our Terms of Service" />
              </ListItem>
              <ListItem>
                <ListItemText primary="In connection with a business transfer or acquisition" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h4" gutterBottom>
          5. International Data Transfers
        </Typography>
        <Typography variant="body1" paragraph>
          Your personal data may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws than your jurisdiction.
        </Typography>
        <Typography variant="body1" paragraph>
          When we transfer personal data from the European Economic Area (EEA) to countries not deemed adequate by the European Commission, we ensure appropriate safeguards are in place, such as:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Standard Contractual Clauses approved by the European Commission" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Adequacy decisions by the European Commission" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Binding Corporate Rules" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Certification schemes and codes of conduct" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h4" gutterBottom>
          6. Data Retention
        </Typography>
        <Typography variant="body1" paragraph>
          GoTruTax retains personal data only for as long as necessary to fulfill the purposes for which it was collected, to comply with legal and regulatory requirements, to resolve disputes, and to enforce our agreements. Retention periods are determined based on the nature of the data, the purpose of processing, and applicable laws. When personal data is no longer required, it will be securely deleted or anonymized in accordance with industry standards and legal requirements. For more details, please refer to our Cookie Policy and contact support@gotrutax.com for specific retention inquiries.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h4" gutterBottom>
          7. Your Rights Under GDPR
        </Typography>
        <Typography variant="body1" paragraph>
          As a data subject located in the EEA, you have the following rights:
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">Right to Access</Typography>
                <Typography variant="body2">Request a copy of your personal data</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">Right to Rectification</Typography>
                <Typography variant="body2">Request correction of inaccurate data</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">Right to Erasure</Typography>
                <Typography variant="body2">Request deletion of your data</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">Right to Restriction</Typography>
                <Typography variant="body2">Limit how we process your data</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">Data Portability</Typography>
                <Typography variant="body2">Receive your data in a structured format</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">Right to Object</Typography>
                <Typography variant="body2">Object to certain data processing</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Typography variant="body1" paragraph sx={{ mt: 2 }}>
          To exercise these rights, please visit our{' '}
          <a href="/gdpr-rights" style={{ color: 'primary.main' }}>GDPR Rights page</a> or contact our Data Protection Officer.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h4" gutterBottom>
          8. Data Security
        </Typography>
        <Typography variant="body1" paragraph>
          We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Encryption: Data encrypted in transit and at rest" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Access Controls: Role-based access to personal data" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Regular Security Audits: Periodic security assessments" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Employee Training: Data protection awareness training" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Incident Response: Procedures for data breaches" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h4" gutterBottom>
          9. Children's Privacy
        </Typography>
        <Typography variant="body1" paragraph>
          Our services are not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16. If we become aware that we have collected personal information from a child under 16, we will take steps to delete such information promptly.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h4" gutterBottom>
          10. Changes to This Privacy Policy
        </Typography>
        <Typography variant="body1" paragraph>
          We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Posting the updated policy on our website" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Sending you an email notification" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Displaying a prominent notice on our website" />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h4" gutterBottom>
          11. Contact Information
        </Typography>
        <Typography variant="body1" paragraph>
          If you have any questions about this Privacy Policy or our data practices, please contact us:
        </Typography>

        <Box sx={{ bgcolor: 'grey.50', p: 3, borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>Data Protection Officer</Typography>
          <Typography variant="body1">
            Email: dpo@gotrutax.com<br />
            Phone: [Your Phone Number]<br />
            Address: [Your Business Address]<br />
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          This Privacy Policy was last updated on November 29, 2025.
        </Typography>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicyPage;


